import React from 'react';
import { useAuth } from '@hooks/useAuth';
import { useHelperFeedEnabled } from '@hooks/useHelperFeedEnabled';
import { useUnreadCount } from '@hooks/useUnreadCount';
import { useNotificationCount } from '@hooks/useNotificationCount';
import { useHelperFeedCount } from '@hooks/useHelperFeedCount';

/**
 * BadgeProvider — owns the lifecycle of badge polling hooks.
 *
 * Mounted once inside AuthProvider + EventStreamProvider so the polling
 * hooks have access to auth and SSE state. Header and BottomNavigation
 * read from the resulting cache via `useNotificationCountValue` /
 * `useHelperFeedCountValue` / `useUnreadCountValue` — read-only subscribers
 * with no polling timer. One QueryObserver per cache key drives the polling;
 * consumers just re-render on cache updates.
 *
 * Why a dedicated provider instead of mounting in AuthProvider:
 * single-responsibility — AuthProvider owns auth state, recovery, and
 * cross-tab sync. Conflating badge polling there bloats it. If we add more
 * background polling later (presence, typing, etc.), they go here.
 *
 * Why this provider exists at all (vs leaving polling in Header/BottomNav):
 * polling lifecycle is now 1:1 with auth lifecycle, never gated by which
 * route components happen to mount. Eliminates the previous gap where
 * detail routes (Header suppressed) needed `PublicLayout` to call
 * `useNotificationCount` itself to keep the cache warm.
 */
export const BadgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const helperFeedEnabled = useHelperFeedEnabled();
  // The hooks gate internally on sessionState (Issue #567 recovery cascade);
  // here we just pass the strict authentication flag so polling stops on logout.
  useUnreadCount(isAuthenticated);
  useNotificationCount(isAuthenticated);
  // Helpable count feeds the combined header bell badge (notifications + helpable);
  // a later `helper_match.created` SSE handler invalidates its key to tick live.
  // Gated on `useHelperFeedEnabled()`: while the feature is off for this viewer,
  // the first param (the query's `enabled` flag) stays false, so no fetch fires
  // and no 10s polling timer runs. The badge stays notifications-only until the
  // viewer is an admin or the flag launches.
  useHelperFeedCount(isAuthenticated && helperFeedEnabled);
  return <>{children}</>;
};
